import { Slot } from '@radix-ui/react-slot'
import type { ComponentPropsWithoutRef } from 'react'

import { cn } from '@/lib/utils'

type ButtonProps = ComponentPropsWithoutRef<'button'> & {
  asChild?: boolean
  variant?: 'primary' | 'secondary' | 'ghost' | 'dark'
  size?: 'sm' | 'md' | 'lg'
}

const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-brand text-white shadow-[0_18px_40px_rgba(92,96,255,0.26)] hover:bg-brand/90',
  secondary:
    'border border-line bg-white text-ink hover:border-brand/25 hover:bg-brand/5',
  ghost: 'bg-transparent text-ink-soft hover:bg-brand/5 hover:text-ink',
  dark: 'border border-white/10 bg-white/10 text-white hover:bg-white/15',
}

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-10 rounded-xl px-4 text-sm',
  md: 'h-11 rounded-2xl px-5 text-sm',
  lg: 'h-12 rounded-2xl px-6 text-sm',
}

export function Button({
  asChild,
  className,
  variant = 'primary',
  size = 'md',
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition duration-200 disabled:cursor-not-allowed disabled:opacity-50',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    />
  )
}
